import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  loadBaseAddressNotification,
  loadCartAddress,
  resetAddressChanged,
  setNewAddress,
} from '../../shared/reducers/shipping-address.slice';
import { useAppSelector } from '../../shared/store';

import { isAllowedUrl } from '../utils/isAllowedUrl';
import { isPC } from '../../../util/window/getDevice';
import { isProduction, KURLY_PRODUCTION_URL_LIST } from '../../shared/configs/config';
import { COMMON_PATH } from '../../shared/constant';
import { removeAddressRegistered } from '../../cart/shared/services/shipping-address.storage.service';
import updateDeliveryTooltipVisibleState from './mainDeliveryTooltipVisibleState';

export default function useDeliveryLocation() {
  const dispatch = useDispatch();
  const router = useRouter();

  const TOOLTIP_DISPLAY_MILLI_SECOND = 3000;

  const { hasSession, isGuest } = useAppSelector(({ auth }) => auth);
  const { addressChanged, currentAddress } = useAppSelector(({ shippingAddress }) => shippingAddress);

  const [isDisabled, setIsDisabled] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [addressId, setAddressId] = useState<string | null>(null);

  const moveLoginPage = () => {
    router.push(`${COMMON_PATH.login.uri}?return_url=${router.asPath}`);
  };

  const toggleAddressLayer = useCallback((value: boolean) => {
    setTooltipOpen(value);
  }, []);

  const detectAddressChanged = useCallback(() => {
    if (addressChanged && !isPC) {
      window.postMessage({ source: 'addressChanged' }, window.location.href);
    }
  }, [addressChanged]);

  const getAddress = useCallback(() => {
    if (!hasSession) {
      return;
    }

    if (isGuest) {
      dispatch(loadCartAddress());
      return;
    }

    dispatch(loadBaseAddressNotification());
  }, [dispatch, isGuest, hasSession]);

  const alertMessage = useCallback(
    async (e: MessageEvent) => {
      if (isProduction() && !KURLY_PRODUCTION_URL_LIST.includes(e.origin)) {
        return;
      }

      if (e.data.source === 'addressChanged') {
        getAddress();
        // 툴팁을 띄우기 위해 id 초기화 처리
        setAddressId('resetId');
      }
    },
    [getAddress],
  );

  const changeAddressStatus = useCallback(() => {
    dispatch(resetAddressChanged());
    dispatch(setNewAddress(false));
  }, [dispatch]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    let timeoutEnd: ReturnType<typeof setTimeout>;

    // 첫 진입 시는 툴팁을 띄우지 않는다.
    if (addressId === null) {
      return;
    }

    const id = String(currentAddress?.id);
    if (id !== addressId) {
      setAddressId(id);
      setTooltipOpen(true);

      timeout = setTimeout(() => {
        setTooltipOpen(false);
        timeoutEnd = setTimeout(() => {
          changeAddressStatus();
          clearTimeout(timeout);
          clearTimeout(timeoutEnd);
        }, 300);
      }, TOOLTIP_DISPLAY_MILLI_SECOND);
    }
  }, [addressId, changeAddressStatus, currentAddress, dispatch]);

  // 메인 페이지 배송지 툴팁 노출 처리
  useEffect(() => {
    if (!currentAddress) {
      return;
    }

    const isMainTooltipVisible = updateDeliveryTooltipVisibleState(router.asPath, !!currentAddress.address);

    if (isMainTooltipVisible) {
      setAddressId('open');
    }
  }, [currentAddress, router.asPath]);

  useEffect(() => {
    if (!hasSession || !router.isReady) {
      return;
    }

    const allowed = isAllowedUrl(router.pathname);

    if (!allowed) {
      setIsDisabled(true);
      return;
    }

    // FIXME: V2 대응 코드 / 메인이 옮겨오면 삭제되어야 함
    removeAddressRegistered();
    getAddress();
    detectAddressChanged();
  }, [dispatch, hasSession, router.isReady, router.pathname, getAddress, detectAddressChanged]);

  useEffect(() => {
    window.addEventListener('message', alertMessage);

    return () => {
      window.removeEventListener('message', alertMessage);
    };
  }, [alertMessage]);

  return {
    moveLoginPage,
    tooltip: {
      isDisabled,
      tooltipOpen,
    },
    toggleAddressLayer,
  };
}
